import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import roomTypesData from '../data/roomTypesData';

const RoomTypes = () => {
    const [activeRoom, setActiveRoom] = useState(1);
    const [ref, inView] = useInView({
        triggerOnce: true,
        threshold: 0.1,
    });

    const selectedRoom = roomTypesData.find(room => room.id === activeRoom);

    return (
        <section id="room-types" className="py-20">
            <div className="container mx-auto px-4">
                <motion.div
                    ref={ref}
                    initial={{ opacity: 0, y: 20 }}
                    animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
                    transition={{ duration: 0.6 }}
                    className="text-center mb-16"
                >
                    <h2 className="text-4xl font-bold text-arcdom-navy mb-4">Our Property Types</h2>
                    <div className="w-20 h-1 bg-arcdom-gold mx-auto mb-6"></div>
                    <p className="max-w-2xl mx-auto text-gray-600">
                        Discover our range of thoughtfully designed living spaces built to the highest standards
                        of quality and comfort.
                    </p>
                </motion.div>

                {/* Room Type Tabs */}
                <div className="flex flex-wrap justify-center mb-12 gap-2 md:gap-4">
                    {roomTypesData.map((room) => (
                        <button
                            key={room.id}
                            onClick={() => setActiveRoom(room.id)}
                            className={`flex items-center px-5 py-3 rounded-full text-sm md:text-base font-medium transition-all duration-300 ${
                                activeRoom === room.id
                                    ? 'bg-arcdom-gold text-white'
                                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                            }`}
                        >
                            <span className="mr-2">{room.icon}</span>
                            {room.name}
                        </button>
                    ))}
                </div>

                {/* Room Display */}
                <AnimatePresence mode="wait">
                    <motion.div
                        key={activeRoom}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -20 }}
                        transition={{ duration: 0.5 }}
                        className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center"
                    >
                        {/* Room Image */}
                        <div className="relative h-96 rounded-lg overflow-hidden shadow-xl">
                            <img
                                src={selectedRoom.image}
                                alt={selectedRoom.name}
                                className="w-full h-full object-cover"
                            />
                            <div className="absolute top-4 left-4 bg-arcdom-navy text-white py-1 px-3 rounded-full text-sm">
                                {selectedRoom.size}
                            </div>
                        </div>

                        {/* Room Details */}
                        <div>
                            <h3 className="text-3xl font-semibold text-arcdom-navy mb-4">
                                {selectedRoom.name}
                            </h3>
                            <p className="text-gray-600 mb-6">{selectedRoom.description}</p>

                            <h4 className="text-xl font-medium text-arcdom-navy mb-3">Features</h4>
                            <ul className="space-y-2 mb-8">
                                {selectedRoom.features.map((feature, index) => (
                                    <motion.li
                                        key={index}
                                        initial={{ opacity: 0, x: -10 }}
                                        animate={{ opacity: 1, x: 0 }}
                                        transition={{ duration: 0.3, delay: index * 0.1 }}
                                        className="flex items-center text-gray-600"
                                    >
                                        <svg className="w-5 h-5 text-arcdom-gold mr-2" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                            <path d="M5 13L9 17L19 7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                        </svg>
                                        {feature}
                                    </motion.li>
                                ))}
                            </ul>

                            <a
                                href="#contact"
                                className="inline-flex items-center px-6 py-3 bg-arcdom-gold text-arcdom-navy rounded-md font-medium hover:bg-opacity-90 transition-all duration-300"
                            >
                                Inquire About {selectedRoom.name}
                                <svg className="w-4 h-4 ml-2" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M5 12H19M19 12L12 5M19 12L12 19" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                </svg>
                            </a>
                        </div>
                    </motion.div>
                </AnimatePresence>
            </div>
        </section>
    );
};

export default RoomTypes;
